import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

function joinNames(items, label) {
  return items.map((item) => item[label] || item.id).join(', ');
}

// What to call the thing being deleted in the "nothing else references this …"
// line. The dependents map alone cannot answer this: a view and a function both
// report `triggers`, so shape detection would call every view a value. Callers
// that know their resource say so with `resourceLabel`; the detection below
// stays for the ones that do not.
function resourceName(dependents, resourceLabel) {
  if (resourceLabel) return resourceLabel;
  if (Object.prototype.hasOwnProperty.call(dependents, 'views')) return 'function';
  if (Object.prototype.hasOwnProperty.call(dependents, 'tables')) return 'category';
  if (Object.prototype.hasOwnProperty.call(dependents, 'export_formats')) return 'template';
  return 'value';
}

function DependencySection({ title, children }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Box>
  );
}

// `preview` is what the backend's dependency check returned for the open target
// (null while it is loading). `scopeChanged` says the confirmation the user gave
// was refused because those dependents changed meanwhile, and `preview` is now
// the fresh scope to confirm again (M228).
export default function DeleteResourceDialog({
  open,
  title,
  preview,
  onConfirm,
  onClose,
  busy,
  resourceLabel = null,
  scopeChanged = false,
  executionError = null,
}) {
  const dependents = preview?.dependents || {};
  // Emails this Function created that have not finished sending. They are already
  // with the provider, so deleting the Function cannot cancel or recall them.
  // This is a warning, not a blocker — but it must never be hidden behind
  // "nothing else references this function".
  const pendingEmails = dependents.pending_emails || [];
  // Runs of this Function already admitted. Deleting the Function stops none of
  // them and removes none from Activity (L79): a run is marked running before it
  // loads the published version, so one already running may finish or may fail;
  // a queued one loads that version when it starts, and fails once the Function
  // is gone.
  const runningRuns = dependents.running_runs || 0;
  const queuedRuns = dependents.queued_runs || 0;
  // A backend from before the split reports one `active_runs` total.
  const activeRuns = runningRuns + queuedRuns || dependents.active_runs || 0;
  const hasDependencies = (
    (dependents.views || []).length > 0
    || (dependents.triggers || []).length > 0
    || (dependents.tables || []).length > 0
    || (dependents.constants || []).length > 0
    || (dependents.rows || []).length > 0
    || (dependents.profiles || []).length > 0
    || (dependents.export_formats || []).length > 0
    || (dependents.value_count || 0) > 0
    || pendingEmails.length > 0
    || activeRuns > 0
  );
  // A View's Automations are managed by that View; a Function's may be managed
  // by some View. Say which relationship the reader is looking at (L65).
  const managedByViewLabel = resourceLabel === 'view' ? ' (managed by this View)' : ' (view-managed)';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {executionError && <Alert severity="error" sx={{ mb: 2 }}>{executionError}</Alert>}
        {preview === null ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Checking dependencies…</Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }} divider={<Divider flexItem />}>
            {scopeChanged && (
              <Alert severity="warning">
                What this deletion affects has changed since you opened it. Review the
                updated list below and confirm again.
              </Alert>
            )}

            {(dependents.views || []).length > 0 && (
              <DependencySection title="Views">
                The function&apos;s cards will be removed: {joinNames(dependents.views, 'name')}.
              </DependencySection>
            )}

            {(dependents.triggers || []).length > 0 && (
              <DependencySection title="Automations">
                This will also delete these automations: {dependents.triggers.map((trigger) => (
                  `${trigger.name || trigger.id}${trigger.managed_by_view ? managedByViewLabel : ''}`
                )).join(', ')}.
              </DependencySection>
            )}

            {(dependents.tables || []).length > 0 && (
              <DependencySection title="Tables">
                Use this category as an attribute column or table attribute: {dependents.tables.map((table) => {
                  if (table.constant) return `${table.id} (table attribute)`;
                  return `${table.id}${table.mandatory ? ' (mandatory)' : ''}`;
                }).join(', ')}.
              </DependencySection>
            )}

            {(dependents.constants || []).length > 0 && (
              <DependencySection title="Table attributes">
                Set as the table attribute of: {joinNames(dependents.constants, 'name')}.
              </DependencySection>
            )}

            {(dependents.rows || []).length > 0 && (
              <DependencySection title="Stored rows">
                Held by rows in: {dependents.rows.map((table) => (
                  `${table.name || table.id} (${table.row_count} row${table.row_count === 1 ? '' : 's'})`
                )).join(', ')}.
              </DependencySection>
            )}

            {(dependents.profiles || []).length > 0 && (
              <DependencySection title="Data profiles">
                The default will be removed: {joinNames(dependents.profiles, 'name')}.
              </DependencySection>
            )}

            {(dependents.export_formats || []).length > 0 && (
              <DependencySection title="Export formats">
                Will be deleted: {joinNames(dependents.export_formats, 'name')}.
              </DependencySection>
            )}

            {(dependents.value_count || 0) > 0 && (
              <DependencySection title="Values">
                {dependents.value_count} categor{dependents.value_count === 1 ? 'y value' : 'y values'} will be deleted.
              </DependencySection>
            )}

            {pendingEmails.length > 0 && (
              <Alert severity="warning">
                {pendingEmails.length} email{pendingEmails.length === 1 ? '' : 's'} created by this
                Function {pendingEmails.length === 1 ? 'is' : 'are'} still pending. Deleting the
                Function will not cancel or recall {pendingEmails.length === 1 ? 'it' : 'them'}.
              </Alert>
            )}

            {activeRuns > 0 && (
              <Alert severity="warning">
                {activeRuns} run{activeRuns === 1 ? '' : 's'} of this Function {activeRuns === 1 ? 'is' : 'are'} in
                progress and will not be stopped.
                {runningRuns > 0 && (
                  ` ${runningRuns} already running ${runningRuns === 1 ? 'keeps' : 'keep'} going without it.`
                )}
                {queuedRuns > 0 && (
                  ` ${queuedRuns} still waiting to start will fail once the Function is gone.`
                )}
                {' '}All of them stay in Activity.
              </Alert>
            )}

            {!hasDependencies && (
              <Typography variant="body2" color="text.secondary">
                Nothing else references this {resourceName(dependents, resourceLabel)}.
              </Typography>
            )}

            {resourceLabel === 'function' && (
              <Typography variant="body2" color="text.secondary">
                Past runs of this Function and their results stay in Activity.
              </Typography>
            )}

            {preview.blocking && (
              <Alert severity="error">
                {dependents.block_message || 'This resource cannot be deleted while it is still in use.'}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {preview?.blocking ? 'Close' : 'Cancel'}
        </Button>
        {preview !== null && !preview.blocking && (
          <Button
            onClick={onConfirm}
            color="error"
            variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {scopeChanged ? 'Confirm again' : 'Delete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
